import { useEffect, useState } from "react";
import { Routes, Route } from "react-router-dom";
import { Home, Receipt, Link2, SquareMousePointer, Calculator, KeyRound, Webhook, Users as UsersIcon, Wallet, Settings } from "lucide-react";
import { AuthProvider, useAuth } from "@/context/auth";
import { LoginFlow } from "@/components/LoginFlow";
import { Shell } from "@/components/Shell";
import { useI18n } from "@/i18n";
import { Card, Spinner, Badge, fmtMoney, fmtDate } from "@/components/ui-rp";
import { TransactionsPage, LinksPage, VposPage } from "./Payments";
import { BalancePage, ApiKeysPage, WebhooksPage, ButtonPage, SettingsPage } from "./Account";
import { MonthlyReport } from "./Report";
import { UsersPage } from "./Users";

const NAV = [
  { path: "", label: "nav.dashboard", icon: Home, testId: "dashboard", el: null },
  { path: "/transacciones", label: "nav.transactions", icon: Receipt, testId: "transactions", el: TransactionsPage },
  { path: "/links", label: "nav.links", icon: Link2, testId: "links", el: LinksPage },
  { path: "/vpos", label: "nav.vpos", icon: Calculator, testId: "vpos", el: VposPage, roles: ["admin", "cashier"] },
  { path: "/boton", label: "nav.button", icon: SquareMousePointer, testId: "button", el: ButtonPage },
  { path: "/saldo", label: "nav.balance", icon: Wallet, testId: "balance", el: BalancePage },
  { path: "/llaves", label: "nav.apikeys", icon: KeyRound, testId: "apikeys", el: ApiKeysPage, roles: ["admin"] },
  { path: "/webhooks", label: "nav.webhooks", icon: Webhook, testId: "webhooks", el: WebhooksPage, roles: ["admin"] },
  { path: "/usuarios", label: "nav.users", icon: UsersIcon, testId: "users", el: null, roles: ["admin"] },
  { path: "/configuracion", label: "nav.settings", icon: Settings, testId: "settings", el: SettingsPage },
];

const Dashboard = () => {
  const { t, lang } = useI18n();
  const { user, client } = useAuth();
  const [tenant, setTenant] = useState(null);
  const [bal, setBal] = useState(null);
  const [orders, setOrders] = useState(null);
  useEffect(() => {
    client.get("/tenant/me").then(({ data }) => setTenant(data)).catch(() => {});
    client.get("/tenant/balance").then(({ data }) => setBal(data)).catch(() => {});
    client.get("/tenant/orders", { params: { limit: 100 } }).then(({ data }) => setOrders(data)).catch(() => setOrders({ items: [], total: 0 }));
  }, [client]);
  const paid = (orders?.items || []).filter((o) => o.status === "paid");
  const metrics = [
    { key: "volume", label: t("dashboard.volume"), value: fmtMoney(paid.reduce((s, o) => s + o.amount, 0)) },
    { key: "txCount", label: t("dashboard.txCount"), value: String(paid.length) },
    { key: "available", label: t("dashboard.available"), value: fmtMoney(bal?.available) },
    { key: "pendingBalance", label: t("dashboard.pendingBalance"), value: fmtMoney(bal?.pending) },
  ];
  return (
    <div className="space-y-6" data-testid="merchant-dashboard">
      <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">{t("dashboard.welcome", { name: user?.name?.split(" ")[0] || "" })}</h1>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {metrics.map((m) => (
          <Card key={m.key} testId={`metric-${m.key}`}><p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{m.label}</p><p className="mt-2 font-heading text-2xl font-extrabold">{m.value}</p></Card>
        ))}
      </div>
      <MonthlyReport />
      <Card testId="recent-orders-card">
        <h2 className="font-heading text-base md:text-lg font-semibold mb-3">{t("nav.transactions")}</h2>
        {!orders ? <Spinner /> : orders.items.length === 0 ? <p className="text-sm text-muted-foreground">{t("tx.empty")}</p> : (
          <ul className="divide-y divide-border">
            {orders.items.slice(0, 5).map((o) => (
              <li key={o.id} className="py-2.5 flex items-center justify-between gap-3 text-sm" data-testid={`recent-order-${o.order_number}`}>
                <div><p className="font-mono font-medium">{o.order_number}</p><p className="text-xs text-muted-foreground">{fmtDate(o.created_at, lang)}</p></div>
                <div className="flex items-center gap-3"><span className="font-bold">{fmtMoney(o.amount)}</span><Badge status={o.status} label={t(`status.${o.status}`)} /></div>
              </li>
            ))}
          </ul>
        )}
      </Card>
      {tenant && (
        <Card testId="tenant-info-card">
          <h2 className="font-heading text-base md:text-lg font-semibold mb-4">{t("dashboard.tenantInfo")}</h2>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 text-sm">
            <div className="flex justify-between sm:block"><dt className="text-muted-foreground">{t("tenants.name")}</dt><dd className="font-medium" data-testid="tenant-name">{tenant.name}</dd></div>
            <div className="flex justify-between sm:block"><dt className="text-muted-foreground">{t("dashboard.slug")}</dt><dd className="font-mono" data-testid="tenant-slug">{tenant.slug}</dd></div>
            <div className="flex justify-between sm:block"><dt className="text-muted-foreground">{t("dashboard.currency")}</dt><dd className="font-medium">{tenant.currency}</dd></div>
            <div className="flex justify-between sm:block"><dt className="text-muted-foreground">{t("dashboard.methodsEnabled")}</dt>
              <dd className="flex gap-2 items-center">
                {tenant.payment_methods?.card && <><img src="/logos/logo_visa.png" alt="Visa" className="h-5 object-contain" /><img src="/logos/logo_mastercard.png" alt="Mastercard" className="h-5 object-contain" /></>}
                {tenant.payment_methods?.yappy && <img src="/logos/logo_yappy.png" alt="Yappy" className="h-5 object-contain" />}
              </dd></div>
          </dl>
        </Card>
      )}
    </div>
  );
};

const Inner = () => {
  const { user, checking, logout, updateMe } = useAuth();
  const { t } = useI18n();
  const imp = JSON.parse(localStorage.getItem("rp_impersonation") || "null");
  if (checking) return <div className="min-h-screen flex items-center justify-center bg-background"><Spinner /></div>;
  if (!user) return <LoginFlow title={t("login.merchantTitle")} testPrefix="merchant-login" allowForgot />;
  const nav = NAV.filter((n) => !n.roles || n.roles.includes(user.role));
  return (
    <Shell navItems={nav} user={user} onLogout={() => { localStorage.removeItem("rp_impersonation"); logout(); }} onLangSaved={(l) => updateMe({ language: l })} basePath="/cuenta" brandLabel={user.tenant?.name}>
      {imp && (
        <div className="mb-4 rounded-xl border border-amber-500/50 bg-amber-500/10 px-4 py-2.5 text-sm flex items-center justify-between gap-3 flex-wrap" data-testid="impersonation-banner">
          <span className="text-amber-800 dark:text-amber-300">{t("admin.impersonating", { by: imp.by })}</span>
          <button type="button" onClick={() => { localStorage.removeItem("rp_impersonation"); logout(); window.close(); }} data-testid="impersonation-exit" className="text-xs font-bold underline">{t("admin.exitImpersonation")}</button>
        </div>
      )}
      <Routes>
        <Route index element={<Dashboard />} />
        <Route path="usuarios" element={<UsersPage />} />
        {nav.filter((n) => n.el).map((n) => <Route key={n.path} path={n.path.slice(1)} element={<n.el />} />)}
      </Routes>
    </Shell>
  );
};

export default function CuentaApp() {
  return (
    <AuthProvider storagePrefix="rp" authBase="/auth">
      <Inner />
    </AuthProvider>
  );
}
